import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import ApiPath from './ConstantApi';
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";

export interface WalletData {
  id?: string;
  data: WalletData;
}

export interface WalletResponse {
  status: string;
  message: string;
  total: number;
  data?: any;
}

export interface WalletListParams {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
  adminStatus?: string;
}

export const WALLET_TAG = 'Wallet' as const;

export const WalletService = createApi({
  reducerPath: 'Wallet',
  baseQuery: RtkBaseQueryWithReauth(process.env.REACT_APP_BASE_URL || ''),
  tagTypes: [WALLET_TAG],
  endpoints: (builder) => ({
    getWalletList: builder.query<WalletResponse, WalletListParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();

        // Add pagination
        queryParams.append('page', params.page.toString());
        queryParams.append('limit', params.limit.toString());

        // Add sorting
        if (params.sortBy) {
          queryParams.append('sortBy', params.sortBy);
        }

        if (params.sortOrder) {
          queryParams.append('sortOrder', params.sortOrder);
        }

        // Add search
        if (params.search) {
          queryParams.append('search', params.search);
        }

        // Add status filter
        if (params.adminStatus) {
          queryParams.append('adminStatus', params.adminStatus === 'All' ? '' : params.adminStatus);
        }


        return {
          url: `${ApiPath.getWalletList}?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: [{ type: WALLET_TAG } as const],
    }),

    updateWallet: builder.mutation<
      { status: string; message: string },
      { id: string; status: string }
    >({
      query: ({ id, status }) => ({
        url: `${ApiPath.updateWallet}/${id}`,
        method: 'PUT',
        body: { status },
      }),
    
      invalidatesTags: [WALLET_TAG],
    }),
    

    getWalletById: builder.query<
      WalletResponse,
      string
    >({
      query: (id) => ({
        url: `${ApiPath.getWalletById}/${id}`,
        method: 'GET',
      }),
      providesTags: [WALLET_TAG],
    }),

    getByUserId: builder.query<
      WalletResponse,
      string
    >({
      query: (id) => ({
        url: `${ApiPath.getByUserId}/${id}`,
        method: 'GET',
      }),
      providesTags: [WALLET_TAG],
    }),


    deleteWallet: builder.mutation<
      { status: string; message: string },
      string
    >({
      query: (id) => ({
        url: `${ApiPath.deleteWallet}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [WALLET_TAG],
    }),
  }),
});


export const {
  useGetWalletListQuery,
  useGetWalletByIdQuery,
  useUpdateWalletMutation,
  useDeleteWalletMutation,
} = WalletService;